const BACKOFFICE_API_URL =
  process.env.REACT_APP_BACKOFFICE_API_URL || "http://localhost:3500";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3505";

const SESSION_KEY = "encuesta4c_individual";

function backofficeUrl() {
  return BACKOFFICE_API_URL.replace(/\/$/, "");
}

function baseUrl() {
  return API_URL.replace(/\/$/, "");
}

export function getStoredSurveySession() {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data?.id_usuario || !data?.id_encuesta) return null;
    return data;
  } catch {
    return null;
  }
}

export function storeSurveySession(session) {
  sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
}

export function clearSurveySession() {
  sessionStorage.removeItem(SESSION_KEY);
}

/**
 * Registra al usuario freemium y crea una encuesta activa con token.
 * Body: nombre, email, pais (ISO-2), genero, oficio, privacidad.
 */
export async function iniciarEncuestaFreemium({
  nombre,
  email,
  pais,
  genero,
  oficio,
  privacidad,
}) {
  try {
    const res = await fetch(`${backofficeUrl()}/gestionEncuestas/iniciarFreemium`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ nombre, email, pais, genero, oficio, privacidad }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      return {
        ok: false,
        messageKey:
          data.code === "PRIVACIDAD_REQUERIDA"
            ? "form.privacyRequired"
            : "errors.startFailed",
        message: data.message || "No se pudo iniciar la encuesta",
      };
    }
    if (!data.token) {
      return { ok: false, messageKey: "errors.tokenFailed" };
    }
    return {
      ok: true,
      token: data.token,
      id_usuario: Number(data.id_usuario),
      id_encuesta: Number(data.id_encuesta),
      nombre_usuario: data.nombre_usuario || String(nombre || "").trim(),
    };
  } catch {
    return { ok: false, messageKey: "errors.connection" };
  }
}

/** Detecta el país del visitante por IP (público). */
export async function detectCountry() {
  try {
    const res = await fetch(`${backofficeUrl()}/gestionEncuestas/detectCountry`);
    if (!res.ok) return null;
    const data = await res.json().catch(() => ({}));
    const code = data?.pais ? String(data.pais).toUpperCase() : null;
    return code && /^[A-Z]{2}$/.test(code) ? code : null;
  } catch {
    return null;
  }
}

/**
 * Legacy self-service (4C backend). Preferir iniciarEncuestaFreemium.
 */
export async function iniciarEncuestaIndividual({ nombre, apellido, email }) {
  try {
    const res = await fetch(`${baseUrl()}/gestionEncuestas/iniciarIndividual`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ nombre, apellido, email }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      return { ok: false, message: data.message || "No se pudo iniciar la encuesta" };
    }
    const session = {
      id_usuario: Number(data.id_usuario),
      id_encuesta: Number(data.id_encuesta),
      nombre_usuario: data.nombre_usuario || `${nombre} ${apellido}`.trim(),
      pais: data.pais || null,
    };
    storeSurveySession(session);
    return { ok: true, session };
  } catch {
    return { ok: false, message: "Error de conexión con el servidor" };
  }
}

export async function fetchPreguntas() {
  try {
    const res = await fetch(`${baseUrl()}/gestionEncuestas/preguntas`);
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
}

export async function guardarRespuestas(ids, respuestas) {
  if (!ids?.id_usuario || !ids?.id_encuesta || !Array.isArray(respuestas) || respuestas.length === 0) {
    return { ok: false, completado: false };
  }
  try {
    const res = await fetch(`${baseUrl()}/gestionEncuestas/guardarRespuestas`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        id_usuario: ids.id_usuario,
        id_encuesta: ids.id_encuesta,
        respuestas: respuestas.map((r) => ({
          id_pregunta: r.id_pregunta,
          valor: Number(r.valor),
        })),
      }),
    });
    if (!res.ok) return { ok: false, completado: false };
    const data = await res.json().catch(() => ({}));
    return { ok: true, completado: Boolean(data.completado) };
  } catch {
    return { ok: false, completado: false };
  }
}

export async function getRespuestas(ids) {
  if (!ids?.id_usuario || !ids?.id_encuesta) return [];
  try {
    const res = await fetch(`${baseUrl()}/gestionEncuestas/getRespuestas`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        id_usuario: ids.id_usuario,
        id_encuesta: ids.id_encuesta,
      }),
    });
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
}

export async function fetchPerfil(ids) {
  if (!ids?.id_usuario || !ids?.id_encuesta) return null;
  try {
    const res = await fetch(`${baseUrl()}/gestionEncuestas/getPerfil`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        id_usuario: ids.id_usuario,
        id_encuesta: ids.id_encuesta,
      }),
    });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}
